import { h } from '../dom';
import type { ScreenCtx } from './context';
import { fader, screenHeader, segmented, toggleSwitch, type ScreenInstance } from './common';
import type { PunchMode, Quality, Settings } from '../../meta/settings';

export function settingsScreen(ctx: ScreenCtx, params: { overlay?: boolean } = {}): ScreenInstance {
  const s = ctx.store.get().settings;
  const commit = (patch: Partial<Settings>) => {
    const next = ctx.store.update((d) => { d.settings = { ...d.settings, ...patch }; });
    ctx.settingsChanged(next.settings);
  };

  const vol = fader(s.masterVolume, (v) => commit({ masterVolume: v }));
  const quality = segmented<Quality>([
    { value: 'low', label: 'LOW', sub: 'Laptops' }, { value: 'med', label: 'MEDIUM', sub: 'Balanced' }, { value: 'high', label: 'HIGH', sub: 'Bloom · shadows' },
  ], s.quality, (v) => commit({ quality: v }), 'sd-segctl--tall');
  const punch = segmented<PunchMode>([
    { value: 'hot', label: 'HOT-PUNCH', sub: '1–9 cut straight to air' },
    { value: 'preview', label: 'PREVIEW → TAKE', sub: '1–9 load preview, SPACE takes' },
  ], s.punchMode, (v) => commit({ punchMode: v }), 'sd-segctl--tall');
  const motion = toggleSwitch(s.reducedMotion, (v) => commit({ reducedMotion: v }));
  const coaching = toggleSwitch(s.coaching, (v) => commit({ coaching: v }));

  let armed = false;
  const resetBtn = h('button', { class: 'sd-btn sd-btn--danger sd-btn--small', attrs: { type: 'button' }, text: 'RESET ALL PROGRESS' });
  resetBtn.addEventListener('click', () => {
    if (!armed) { armed = true; resetBtn.textContent = 'CLICK AGAIN TO CONFIRM'; resetBtn.classList.add('is-armed'); setTimeout(() => { armed = false; resetBtn.textContent = 'RESET ALL PROGRESS'; resetBtn.classList.remove('is-armed'); }, 3000); return; }
    const d = ctx.store.reset();
    ctx.settingsChanged(d.settings);
    vol.set(d.settings.masterVolume); quality.set(d.settings.quality); punch.set(d.settings.punchMode); motion.set(d.settings.reducedMotion); coaching.set(d.settings.coaching);
    ctx.notify('PROGRESS RESET');
    armed = false; resetBtn.textContent = 'RESET ALL PROGRESS'; resetBtn.classList.remove('is-armed');
  });

  const row = (label: string, hint: string, control: HTMLElement) =>
    h('div', { class: 'sd-setrow' }, [h('div', { class: 'sd-setrow__text' }, [h('b', { text: label }), h('span', { text: hint })]), control]);

  const el = h('section', { class: `sd-screen sd-page sd-settings ${params.overlay ? 'sd-screen--overlay' : ''}` }, [
    screenHeader('SETTINGS', { kicker: 'TRUCK CONFIGURATION', onBack: () => ctx.back() }),
    h('div', { class: 'sd-settings__body sd-panel' }, [
      h('div', { class: 'sd-section-head' }, [h('span', { text: 'AUDIO' })]),
      row('MASTER VOLUME', 'Crowd, whistle, stings and booth.', vol.el),
      h('div', { class: 'sd-section-head' }, [h('span', { text: 'VIDEO' })]),
      row('QUALITY', 'Lower it if the monitor wall stutters.', quality.el),
      row('REDUCED MOTION', 'Calms shakes, slams and sweeps.', motion.el),
      h('div', { class: 'sd-section-head' }, [h('span', { text: 'CONTROLS' })]),
      row('NUMBER KEYS', 'Clicking a monitor follows the same rule. Shift does the other.', punch.el),
      row('COACHING TIPS', 'Producer tips after mistakes in Rookie matches. Never shown on Pro or Legend.', coaching.el),
      h('div', { class: 'sd-section-head' }, [h('span', { text: 'DATA' })]),
      row('SAVE DATA', ctx.store.persistent ? 'Stored in this browser.' : 'Browser storage unavailable — progress lasts this session only.', resetBtn),
    ]),
  ]);
  return { el, overlay: params.overlay };
}
